import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { FaClipboard, FaExchangeAlt, FaPlaneArrival, FaPlaneDeparture, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Data from './Data';
import { FinField, FinInput, FinNumber, FinSwitch } from './FinField';
import { AVISO_CLIPBOARD_INVALIDO, COPIAS, lerParaColar } from './MenuContextoMalha';
import { createEmptyOpcao, calcularTotal, camposDoColar, ladosDaCopia, somaMoeda } from '../util/malhaAereo';
import { moeda } from '../util/util';
import '../css/financeiro-parcela-pagar.css';
import '../css/menu-contexto-malha.css';


// Tarifa e taxas não seguem o prefixo do lado (tarifaIda / tarifaRet), por isso vão nomeadas.
const LADOS = [
    { chave: 'ida',     rotulo: 'Ida',   p: 'ida', tarifa: 'tarifaIda', taxas: 'taxasIda' },
    { chave: 'retorno', rotulo: 'Volta', p: 'ret', tarifa: 'tarifaRet', taxas: 'taxasRet' },
];
const PONTAS = ['Origem', 'Destino'];
const LOCAL = ['Iata', 'Cidade', 'Aeroporto'];
const camposTexto = (p) => [...PONTAS.flatMap(pt => LOCAL.map(s => p + pt + s)),
    p + 'Data', p + 'Cia', p + 'Voo', p + 'Saida', p + 'Chegada'];

const preenchido = (v) => String(v ?? '').trim() !== '';
const iataOk = (v) => /^[A-Z]{3}$/.test(String(v ?? ''));
const dataValida = (v) => moment(String(v ?? ''), 'DD/MM/YYYY', true).isValid();
// A ponta precisa do que a tela mostra dela: a IATA (rota da planilha e chip do board) ou o
// aeroporto (Origem/Destino da planilha compacta). Só a cidade deixaria a linha com "?" no board
// e o Destino vazio na planilha. IATA pela metade trava.
const pontaOk = (f, p, pt) => {
    const sigla = f[p + pt + 'Iata'];
    if (preenchido(sigla) && !iataOk(sigla)) return false;
    return iataOk(sigla) || preenchido(f[p + pt + 'Aeroporto']);
};
const ladoOk = (f, l) => PONTAS.every(pt => pontaOk(f, l.p, pt))
    && dataValida(f[l.p + 'Data']) && preenchido(f[l.p + 'Cia']) && preenchido(f[l.p + 'Voo']);
// Mesmo texto que o upload grava (normalizarMoedas no back): "R$ 1.234,56".
const textoMoeda = (n) => (n == null ? '' : 'R$ ' + moeda(n));
// Moeda fica NÚMERO no form (o FinNumber não aceita string) e vira texto só ao adicionar.
const formInicial = () => ({ ...createEmptyOpcao(), ida: true, retorno: true,
    tarifaIda: null, taxasIda: null, tarifaRet: null, taxasRet: null });

// O Colar, com as opções e as regras do Colar da malha. O ícone de cada opção, pelos lados dela.
const ICONE_COLAR = { ida: <FaPlaneDeparture size={12} />, ret: <FaPlaneArrival size={12} />, 'ida,ret': <FaExchangeAlt size={12} /> };
// O âmbar da prévia do Colar da malha: o lado que vai receber, já com o que vai entrar.
const PREVIA_COLAR = { boxShadow: 'inset 0 0 0 9999px rgba(245, 158, 11, 0.14)', borderColor: 'rgba(245, 158, 11, 0.7)' };

// Por que colar aqueles lados não vale. Os lados da cópia saem do conteúdo (`ladosDaCopia`), e um lado
// só vai como Ida ou como Volta.
const motivoNaoColar = (fonte, lados) => {
    const vieram = ladosDaCopia(fonte);
    if (!vieram.length) return 'A cópia não tem voo.';
    if (lados.length > 1 && vieram.length === 1) return 'Veio um lado só: cole como Ida ou como Volta.';
    return null;
};

// O formulário com o Colar daqueles lados, da primeira linha copiada (`camposDoColar`, o mesmo da
// malha: um lado só vai para o escolhido). O lado que recebe algum campo liga; o que não recebe e está
// vazio desliga.
const comColagem = (f, fonte, lados) => {
    const campos = camposDoColar(fonte.itens[0], lados, fonte);
    const novo = { ...f };
    LADOS.forEach(l => {
        const chaves = [...camposTexto(l.p), l.tarifa, l.taxas];
        if (!chaves.some(k => preenchido(campos[k]))) {
            if (!chaves.some(k => preenchido(f[k]))) novo[l.chave] = false;
            return;
        }
        novo[l.chave] = true;
        camposTexto(l.p).forEach(k => { if (k in campos) novo[k] = String(campos[k] ?? ''); });
        // Moeda vira número, como o FinNumber pede; itinerário com várias linhas soma.
        [l.tarifa, l.taxas].forEach(k => {
            if (k in campos) novo[k] = preenchido(campos[k]) ? somaMoeda(campos[k]) : null;
        });
    });
    return novo;
};

/**
 * Nova linha da malha: pergunta o voo e devolve a opção pronta. Nada vai ao banco: a página
 * relaciona a opção pelo vínculo automático, põe no state (sem `id`) e só o Salvar grava.
 * Cancelar não deixa rastro.
 *
 * O Colar abre um menu com Ida, Volta e Ida & Volta, nas regras do Colar da malha. O mouse numa opção
 * mostra no formulário, em âmbar, o que vai entrar, e o clique preenche. Vale a primeira linha copiada
 * — do clipboard, quando veio de uma planilha, ou da cópia do clique direito da malha —, e o Colar não
 * adiciona: quem adiciona é o Adicionar, depois de conferir.
 *
 * @param {boolean}  aberto
 * @param {Function} onFechar    () => void
 * @param {Function} onAdicionar (opcao) => void — a opção no formato do createEmptyOpcao
 */
export default function NovaOpcaoMalhaModal({ aberto, onFechar, onAdicionar }) {
    const [form, setForm] = useState(null);
    // O menu do Colar: a cópia lida ao abrir, e os lados sob o mouse, cuja prévia o formulário mostra.
    const [colagem, setColagem] = useState(null);
    const [previa, setPrevia] = useState(null);
    const colarRef = useRef(null);
    // Reabre sempre limpo. Fechado volta a null: sem isso a reabertura pintava um quadro com o
    // form da vez anterior antes de o efeito limpar.
    useEffect(() => {
        setForm(aberto ? formInicial() : null);
        setColagem(null);
        setPrevia(null);
    }, [aberto]);
    // O menu do Colar fecha no Esc e no clique fora dele, como o do clique direito.
    useEffect(() => {
        if (!colagem) return undefined;
        const fechar = () => { setColagem(null); setPrevia(null); };
        const aoClicar = (e) => { if (colarRef.current && !colarRef.current.contains(e.target)) fechar(); };
        const aoTeclar = (e) => { if (e.key === 'Escape') fechar(); };
        document.addEventListener('mousedown', aoClicar);
        document.addEventListener('keydown', aoTeclar);
        return () => {
            document.removeEventListener('mousedown', aoClicar);
            document.removeEventListener('keydown', aoTeclar);
        };
    }, [colagem]);
    if (!aberto || !form) return null;

    const set = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
    const algumLado = LADOS.some(l => form[l.chave]);
    const valido = algumLado && LADOS.every(l => !form[l.chave] || ladoOk(form, l));

    // Lado desligado não leva nada: o texto fica no form só para voltar se o switch religar.
    const adicionar = () => {
        const nova = createEmptyOpcao();
        LADOS.filter(l => form[l.chave]).forEach(l => {
            camposTexto(l.p).forEach(k => { nova[k] = String(form[k] ?? '').trim(); });
            nova[l.tarifa] = textoMoeda(form[l.tarifa]);
            nova[l.taxas] = textoMoeda(form[l.taxas]);
        });
        nova.total = calcularTotal(nova); // como o fundir (malhaAereo.js:582)
        onAdicionar(nova);
    };

    // Abre o menu do Colar com o que está no clipboard, lido como o do clique direito da malha
    // (`lerParaColar`): o texto de fora reconhecido vira a cópia, e o não reconhecido avisa em vez de
    // colar a cópia velha. Aberto, o botão fecha.
    const abrirColar = async () => {
        if (colagem) {
            setColagem(null);
            setPrevia(null);
            return;
        }
        const { copia: fonte, invalido } = await lerParaColar();
        if (invalido) {
            toast.error(AVISO_CLIPBOARD_INVALIDO);
            return;
        }
        if (!fonte || !fonte.itens.length) {
            toast.warning('Nada para colar: copie uma linha da malha ou de uma planilha.');
            return;
        }
        setColagem({ fonte });
    };
    const colar = (lados) => {
        const { fonte } = colagem;
        setForm(prev => comColagem(prev, fonte, lados));
        setColagem(null);
        setPrevia(null);
        if (fonte.itens.length > 1) toast.info(`Preenchido com a primeira das ${fonte.itens.length} linhas copiadas.`);
    };
    // Com o mouse numa opção do Colar, o formulário mostra o que vai entrar; o form só muda no clique.
    const exibido = colagem && previa ? comColagem(form, colagem.fonte, previa) : form;
    const rotuloCopia = (fonte) => {
        const n = fonte.itens.length;
        return `${ladosDaCopia(fonte).length === 1 ? 'Um lado de ' : ''}${n} ${n !== 1 ? 'linhas copiadas · vale a primeira' : 'linha copiada'}`;
    };

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg"
            // Rola o fundo, e não o corpo: sem isto o rodapé some quando os dois lados passam da
            // altura da janela. O porquê completo está no GrupoMalhaModal.
            style={{ overflowY: 'auto' }}
            onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            {/* A largura do modal de grupos (`GrupoMalhaModal`): os dois lados cabem lado a lado com a IATA e o
                aeroporto, a data, a cia e o voo, os horários e os valores numa linha cada. */}
            <div className="modal-dialog modal-dialog-centered modal-lg"
                style={{ maxWidth: 'min(100% - 1rem, 1085px)' }}>
                {/* --flow: o calendário do Data vaza o card sem corte. */}
                <div className="modal-content fin-pp__modal fin-pp__modal--flow">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaPlus size={15} /></span>
                        <h3 className="fin-pp__modal-title">Nova linha
                            <span className="fin-pp__modal-sub">Voo de uma cotação que não veio na planilha. A linha vai para o trecho que casar, ou para as órfãs, e só é gravada no Salvar.</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar}>
                            <FaTimes size={16} />
                        </button>
                    </div>

                    <div className="modal-body" style={{ minHeight: 330 }}>
                        <div className="fin-pp__trecho-sentidos" style={{ marginTop: 0 }}>
                            {/* Os campos mostram o `exibido`: o form, ou a prévia do Colar sob o mouse. */}
                            {LADOS.map(l => (
                                <div key={l.chave} className="fin-pp__trecho-sentido"
                                    style={colagem && previa && previa.includes(l.p) ? PREVIA_COLAR : undefined}>
                                    <FinSwitch label={l.rotulo} checked={!!exibido[l.chave]}
                                        onChange={e => set(l.chave, e.target.checked)} />
                                    {exibido[l.chave] && (<>
                                        {/* Cada ponta em duas linhas: a IATA com o aeroporto, e a cidade. */}
                                        {PONTAS.map(pt => (
                                            <React.Fragment key={pt}>
                                                <div className="fin-pp__filters">
                                                    <FinField label={pt + ' (IATA)'} style={{ flex: '0 0 110px', minWidth: 110 }}>
                                                        <FinInput maxLength={3} placeholder="GRU" value={exibido[l.p + pt + 'Iata']}
                                                            onChange={e => set(l.p + pt + 'Iata', e.target.value.replace(/[^a-z]/gi, '').toUpperCase())} />
                                                    </FinField>
                                                    <FinField label="Aeroporto" style={{ flex: '1 1 200px', minWidth: 120 }}>
                                                        <FinInput value={exibido[l.p + pt + 'Aeroporto']}
                                                            onChange={e => set(l.p + pt + 'Aeroporto', e.target.value)} />
                                                    </FinField>
                                                </div>
                                                <div className="fin-pp__filters">
                                                    <FinField label="Cidade" full>
                                                        <FinInput value={exibido[l.p + pt + 'Cidade']}
                                                            onChange={e => set(l.p + pt + 'Cidade', e.target.value)} />
                                                    </FinField>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                        <div className="fin-pp__filters">
                                            {/* Sem rótulo próprio: o do campo leva o asterisco, como no FormTrecho. */}
                                            <FinField label="Data" required style={{ flex: '0 1 150px', minWidth: 130 }}>
                                                <Data labelExterno label="" value={exibido[l.p + 'Data']}
                                                    onChange={v => set(l.p + 'Data', v)} />
                                            </FinField>
                                            <FinField label="Cia" required style={{ flex: '1 1 90px', minWidth: 80 }}>
                                                <FinInput value={exibido[l.p + 'Cia']} onChange={e => set(l.p + 'Cia', e.target.value)} />
                                            </FinField>
                                            <FinField label="Voo" required style={{ flex: '1 1 90px', minWidth: 80 }}>
                                                <FinInput value={exibido[l.p + 'Voo']} onChange={e => set(l.p + 'Voo', e.target.value)} />
                                            </FinField>
                                        </div>
                                        <div className="fin-pp__filters">
                                            {[['Saida', 'Saída'], ['Chegada', 'Chegada']].map(([k, rot]) => (
                                                <FinField key={k} label={rot} style={{ flex: '1 1 150px', minWidth: 110 }}>
                                                    <FinInput type="time" value={exibido[l.p + k] ?? ''}
                                                        onChange={e => set(l.p + k, e.target.value)} />
                                                </FinField>
                                            ))}
                                        </div>
                                        <div className="fin-pp__filters">
                                            {/* Só a digitação grava: o NumericFormat também avisa quando o valor muda por
                                                fora, e a prévia do Colar entraria no form sem o clique. */}
                                            {[[l.tarifa, 'Tarifa'], [l.taxas, 'Taxas']].map(([k, rot]) => (
                                                <FinField key={k} label={rot} style={{ flex: '1 1 150px', minWidth: 120 }}>
                                                    <FinNumber prefix="R$ " placeholder="R$ 0,00" allowNegative={false}
                                                        value={exibido[k] ?? ''}
                                                        onValueChange={({ floatValue }, { source }) => source === 'event' && set(k, floatValue ?? null)} />
                                                </FinField>
                                            ))}
                                        </div>
                                    </>)}
                                </div>
                            ))}
                        </div>
                        {!valido && (
                            <p className="fin-pp__cell-muted" style={{ margin: '8px 0 0' }}>
                                {algumLado
                                    ? 'Em cada lado ligado, preencha origem e destino (IATA de 3 letras ou aeroporto), data, cia e voo.'
                                    : 'Ligue a ida, a volta ou as duas.'}
                            </p>
                        )}
                    </div>

                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onFechar}>Cancelar</button>
                        {/* O menu do Colar abre acima do botão, no desenho do menu do clique direito. */}
                        <span ref={colarRef} style={{ position: 'relative', display: 'inline-flex' }}>
                            <button type="button" className="fin-pp__btn" onClick={abrirColar} aria-expanded={!!colagem}
                                title="Preenche os campos com a linha copiada, da malha ou de uma planilha: escolha a ida, a volta ou as duas, e confira antes de Adicionar.">
                                <FaClipboard size={12} /> Colar
                            </button>
                            {colagem && (
                                <div className="fin-pp__mctx" role="menu" aria-label="Colar"
                                    style={{ position: 'absolute', left: 0, top: 'auto', bottom: 'calc(100% + 6px)', width: 260 }}>
                                    <div className="fin-pp__mctx-alvo">{rotuloCopia(colagem.fonte)}</div>
                                    <div className="fin-pp__mctx-body">
                                        {COPIAS.map(c => {
                                            const motivo = motivoNaoColar(colagem.fonte, c.lados);
                                            const prever = motivo ? {} : {
                                                onMouseEnter: () => setPrevia(c.lados),
                                                onMouseLeave: () => setPrevia(null),
                                                onFocus: () => setPrevia(c.lados),
                                                onBlur: () => setPrevia(null),
                                            };
                                            return (
                                                <button key={c.rotulo} type="button" role="menuitem" className="fin-pp__mctx-item"
                                                    disabled={!!motivo} title={motivo || undefined}
                                                    {...prever}
                                                    onClick={() => colar(c.lados)}>
                                                    <span className="fin-pp__mctx-item-ic">{ICONE_COLAR[c.lados.join()]}</span>
                                                    <span className="fin-pp__mctx-item-txt">
                                                        {c.rotulo}
                                                        {motivo && <span className="fin-pp__mctx-item-sub">{motivo}</span>}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </span>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" disabled={!valido} onClick={adicionar}>
                            <FaPlus size={12} /> Adicionar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
